import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DepartmentForm from './DepartmentForm';
import {
    Building,
    PlusCircle,
    Edit,
    Trash,
    AlertCircle,
    CheckCircle,
    Users,
    ShieldCheck,
    ArrowRightLeft,
    CheckCircle2,
    XCircle
} from 'lucide-react';
import { apiClient } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import DataTable from './common/DataTable';
import InfoModal from './common/InfoModal';

export default function DepartmentManagement() {
    const [departmentList, setDepartmentList] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    // Silme & Aktarım Modal State'leri
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [departmentToDelete, setDepartmentToDelete] = useState(null);
    const [transferTargetId, setTransferTargetId] = useState('');
    const [deleteModalError, setDeleteModalError] = useState(null);
    const [forceTransferRequired, setForceTransferRequired] = useState(false);

    // Sadece Personel/Yetki Aktarımı (Bölümü Silmeden) Modal State'i
    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
    const [departmentToTransfer, setDepartmentToTransfer] = useState(null);
    const [transferOnlyTargetId, setTransferOnlyTargetId] = useState('');
    const [transferDataScope, setTransferDataScope] = useState(true);
    const [transferModalError, setTransferModalError] = useState(null);

    const { isAuthenticated, permissions = [], activeSubCompanyCode } = useAuth();

    const canView = useMemo(() => permissions.includes('departments:view_list'), [permissions]);
    const canCreate = useMemo(() => permissions.includes('departments:create'), [permissions]);
    const canUpdate = useMemo(() => permissions.includes('departments:update'), [permissions]);
    const canDelete = useMemo(() => permissions.includes('departments:delete'), [permissions]);

    const fetchDepartments = useCallback(async () => {
        if (!canView) {
            setError("Bölümleri listeleme yetkiniz bulunmamaktadır.");
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await apiClient('Department');
            setDepartmentList(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Bölümler çekilirken hata oluştu:", err);
            setError(err.message || "Bölümleri getirirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    }, [canView, activeSubCompanyCode]);

    useEffect(() => {
        if (isAuthenticated) {
            fetchDepartments();
        }
    }, [isAuthenticated, fetchDepartments]);

    const summaryStats = useMemo(() => {
        const total = departmentList.length;
        const activeDepts = departmentList.filter(d => d.isActive).length;
        const passiveDepts = total - activeDepts;
        const totalActiveEmployees = departmentList.reduce((acc, d) => acc + (d.activeEmployeeCount || 0), 0);
        const scopedDepts = departmentList.filter(d => (d.authorizedUserCount || 0) > 0).length;
        return { total, activeDepts, passiveDepts, totalActiveEmployees, scopedDepts };
    }, [departmentList]);

    const handleAddClick = () => {
        setSelectedDepartment(null);
        setIsModalOpen(true);
        setMessage(null);
        setError(null);
    };

    const handleEditClick = (department) => {
        setSelectedDepartment(department);
        setIsModalOpen(true);
        setMessage(null);
        setError(null);
    };

    const handleTransferClick = (department) => {
        setDepartmentToTransfer(department);
        setTransferOnlyTargetId('');
        setTransferDataScope(true);
        setTransferModalError(null);
        setIsTransferModalOpen(true);
        setMessage(null);
        setError(null);
    };

    const handleDeleteRequest = (department) => {
        setDepartmentToDelete(department);
        setTransferTargetId('');
        setDeleteModalError(null);
        setForceTransferRequired((department.totalEmployeeCount || 0) > 0);
        setIsConfirmModalOpen(true);
        setMessage(null);
        setError(null);
    };

    const handleDeleteConfirm = async () => {
        if (!departmentToDelete) return;

        const needsTransfer = forceTransferRequired || (departmentToDelete.totalEmployeeCount || 0) > 0;
        if (needsTransfer && !transferTargetId) {
            setDeleteModalError("Bağlı personellerin ve veri kapsamı yetkilerinin aktarılacağı hedef bölümü seçmelisiniz.");
            return;
        }

        setActionLoading(true);
        setDeleteModalError(null);
        try {
            const endpoint = transferTargetId
                ? `Department/${departmentToDelete.id}?transferToDepartmentId=${transferTargetId}`
                : `Department/${departmentToDelete.id}`;

            await apiClient(endpoint, { method: 'DELETE' });
            setIsConfirmModalOpen(false);
            setDepartmentToDelete(null);
            setTransferTargetId('');
            setForceTransferRequired(false);
            setMessage(
                transferTargetId
                    ? `"${departmentToDelete.name}" bölümündeki personeller ve veri kapsamı yetkileri hedef bölüme aktarıldı ve bölüm silindi.`
                    : `"${departmentToDelete.name}" bölümü başarıyla silindi.`
            );
            await fetchDepartments();
        } catch (err) {
            const msg = err.message || "Bölüm silinirken bir hata oluştu.";
            if (msg.includes("personel") || msg.includes("arşivlenmiş") || msg.includes("aktar")) {
                setForceTransferRequired(true);
            }
            setDeleteModalError(msg);
        } finally {
            setActionLoading(false);
        }
    };

    const handleTransferConfirm = async () => {
        if (!departmentToTransfer) return;
        if (!transferOnlyTargetId) {
            setTransferModalError("Lütfen personellerin aktarılacağı hedef bölümü seçin.");
            return;
        }

        setActionLoading(true);
        setTransferModalError(null);
        try {
            const res = await apiClient(`Department/${departmentToTransfer.id}/transfer`, {
                method: 'POST',
                body: {
                    targetDepartmentId: Number(transferOnlyTargetId),
                    transferDataScopeAccess: transferDataScope
                }
            });
            setIsTransferModalOpen(false);
            setDepartmentToTransfer(null);
            setTransferOnlyTargetId('');
            setMessage(res?.message || "Bölüm personelleri başarıyla aktarıldı.");
            await fetchDepartments();
        } catch (err) {
            setTransferModalError(err.message || "Personel aktarımı sırasında bir hata oluştu.");
        } finally {
            setActionLoading(false);
        }
    };

    const handleFormSubmit = async (departmentData) => {
        setActionLoading(true);
        setError(null);
        try {
            const method = departmentData.id ? 'PUT' : 'POST';
            const endpoint = departmentData.id ? `Department/${departmentData.id}` : 'Department';
            await apiClient(endpoint, {
                method,
                body: departmentData,
            });
            setMessage(`"${departmentData.name}" bölümü başarıyla ${departmentData.id ? 'güncellendi' : 'eklendi'}!`);
            setIsModalOpen(false);
            setSelectedDepartment(null);
            await fetchDepartments();
        } catch (err) {
            throw err;
        } finally {
            setActionLoading(false);
        }
    };

    const handleModalClose = () => {
        if (actionLoading) return;
        setIsModalOpen(false);
        setIsConfirmModalOpen(false);
        setIsTransferModalOpen(false);
        setSelectedDepartment(null);
        setDepartmentToDelete(null);
        setDepartmentToTransfer(null);
        setTransferTargetId('');
        setTransferOnlyTargetId('');
        setDeleteModalError(null);
        setTransferModalError(null);
        setForceTransferRequired(false);
    };

    const availableTargetDepartmentsForDelete = useMemo(() => {
        if (!departmentToDelete) return [];
        return departmentList.filter(d => d.id !== departmentToDelete.id && d.isActive);
    }, [departmentList, departmentToDelete]);

    const availableTargetDepartmentsForTransfer = useMemo(() => {
        if (!departmentToTransfer) return [];
        return departmentList.filter(d => d.id !== departmentToTransfer.id && d.isActive);
    }, [departmentList, departmentToTransfer]);

    const departmentColumns = useMemo(() => {
        const columns = [
            {
                header: 'Bölüm Adı',
                accessor: 'name',
                sortable: true,
                searchable: true,
                render: (row) => (
                    <div className="flex items-center gap-2.5">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                            row.isActive
                                ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400'
                                : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                        }`}>
                            <Building className="w-4 h-4" />
                        </div>
                        <div>
                            <div className="font-semibold text-gray-900 dark:text-gray-100">{row.name}</div>
                            <div className="text-[11px] text-gray-400 dark:text-gray-500">Kod: #{row.id}</div>
                        </div>
                    </div>
                )
            },
            {
                header: 'Açıklama',
                accessor: 'description',
                sortable: true,
                searchable: true,
                render: (row) => (
                    <span className="text-gray-600 dark:text-gray-300 text-xs whitespace-normal line-clamp-2 max-w-md">
                        {row.description || '-'}
                    </span>
                )
            },
            {
                header: 'Personel Sayısı',
                accessor: 'activeEmployeeCount',
                sortable: true,
                searchable: false,
                getFilterValue: (row) => String(row.activeEmployeeCount ?? 0),
                render: (row) => {
                    const active = row.activeEmployeeCount || 0;
                    const inactive = row.inactiveEmployeeCount || 0;
                    return (
                        <div className="flex items-center gap-1.5">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                                active > 0
                                    ? 'bg-blue-50 text-blue-700 border border-blue-200/80 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/60'
                                    : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                            }`}>
                                <Users className="w-3.5 h-3.5" />
                                {active.toLocaleString('tr-TR')} Aktif
                            </span>
                            {inactive > 0 && (
                                <span
                                    className="text-[11px] text-gray-400 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/70 px-2 py-0.5 rounded-full"
                                    title="İşten ayrılmış / pasif personel sayısı"
                                >
                                    +{inactive} Pasif
                                </span>
                            )}
                        </div>
                    );
                }
            },
            {
                header: 'Veri Kapsamı (DataScope)',
                accessor: 'authorizedUserCount',
                sortable: true,
                searchable: false,
                getFilterValue: (row) => String(row.authorizedUserCount ?? 0),
                render: (row) => {
                    const authCount = row.authorizedUserCount || 0;
                    return authCount > 0 ? (
                        <span
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200/80 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800/60"
                            title="Bu bölümü görmeye yetkili (DataScope atanmış) kullanıcı sayısı"
                        >
                            <ShieldCheck className="w-3.5 h-3.5" />
                            {authCount} Yetkili Kullanıcı
                        </span>
                    ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">Genel (Tüm Veri)</span>
                    );
                }
            },
            {
                header: 'Durum',
                accessor: 'isActive',
                sortable: true,
                searchable: false,
                getFilterValue: (row) => row.isActive ? 'Aktif' : 'Pasif',
                render: (row) => row.isActive ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/60">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Aktif
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600">
                        <XCircle className="w-3.5 h-3.5" /> Pasif
                    </span>
                )
            },
        ];

        if (canUpdate || canDelete) {
            columns.push({
                header: 'Aksiyonlar',
                accessor: 'actions',
                filterable: false,
                render: (row) => (
                    <div className="flex items-center space-x-1.5">
                        {canUpdate && (
                            <button
                                onClick={() => handleEditClick(row)}
                                className="p-1.5 rounded-lg text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-gray-700 transition-colors"
                                title="Bölümü Düzenle"
                            >
                                <Edit className="w-4 h-4" />
                            </button>
                        )}
                        {canUpdate && ((row.totalEmployeeCount || 0) > 0 || (row.authorizedUserCount || 0) > 0) && (
                            <button
                                onClick={() => handleTransferClick(row)}
                                className="p-1.5 rounded-lg text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-gray-700 transition-colors"
                                title="Personelleri ve Veri Kapsamını Başka Bölüme Aktar"
                            >
                                <ArrowRightLeft className="w-4 h-4" />
                            </button>
                        )}
                        {canDelete && (
                            <button
                                onClick={() => handleDeleteRequest(row)}
                                className="p-1.5 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-gray-700 transition-colors"
                                title="Bölümü Sil"
                            >
                                <Trash className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                ),
            });
        }
        return columns;
    }, [canUpdate, canDelete]);

    if (!canView && !loading) {
        return (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200 rounded-xl flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 shrink-0" />
                Bu sayfayı görüntüleme yetkiniz bulunmamaktadır.
            </div>
        );
    }

    const deleteNeedsTransfer = departmentToDelete && (forceTransferRequired || (departmentToDelete.totalEmployeeCount || 0) > 0);

    return (
        <div className="p-1 space-y-4">
            {/* Üst Özet Kartları */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200/70 dark:border-gray-700/70 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Toplam Bölüm</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-0.5">
                            {summaryStats.total.toLocaleString('tr-TR')}
                        </p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                            <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{summaryStats.activeDepts} Aktif</span>
                            {summaryStats.passiveDepts > 0 && ` • ${summaryStats.passiveDepts} Pasif`}
                        </p>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                        <Building className="w-6 h-6" />
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200/70 dark:border-gray-700/70 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Bölümlerdeki Aktif Personel</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-0.5">
                            {summaryStats.totalActiveEmployees.toLocaleString('tr-TR')}
                        </p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                            Aktif çalışanların bölüm dağılımı
                        </p>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                        <Users className="w-6 h-6" />
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200/70 dark:border-gray-700/70 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Veri Kapsamı (DataScope) Filtreli Bölüm</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-0.5">
                            {summaryStats.scopedDepts.toLocaleString('tr-TR')}
                        </p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                            Bölüm bazlı yetkilendirilmiş birimler
                        </p>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                        <ShieldCheck className="w-6 h-6" />
                    </div>
                </div>
            </div>

            {message && (
                <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 p-3.5 rounded-xl flex items-center justify-between">
                    <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 mr-2.5 text-emerald-500 shrink-0" />
                        <span className="text-sm font-medium">{message}</span>
                    </div>
                    <button onClick={() => setMessage(null)} className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline ml-4">Kapat</button>
                </div>
            )}

            {error && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 p-3.5 rounded-xl flex items-center justify-between">
                    <div className="flex items-center">
                        <AlertCircle className="w-5 h-5 mr-2.5 text-red-500 shrink-0" />
                        <span className="text-sm font-medium">{error}</span>
                    </div>
                    <button onClick={() => setError(null)} className="text-xs text-red-600 dark:text-red-400 hover:underline ml-4">Kapat</button>
                </div>
            )}

            <DataTable
                data={departmentList}
                columns={departmentColumns}
                itemsPerPage={10}
                pageSizeOptions={[10, 20, 50, 100]}
                showSearch={true}
                searchPlaceholder="Bölüm adı veya açıklamada ara..."
                showPagination={true}
                emptyMessage="Henüz hiçbir bölüm eklenmemiş."
                isLoading={loading}
                errorMessage={null}
                actionsHeader={
                    canCreate && (
                        <button
                            onClick={handleAddClick}
                            className="flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors"
                        >
                            <PlusCircle className="w-4 h-4 mr-2" />
                            Yeni Bölüm Ekle
                        </button>
                    )
                }
            />

            {/* Bölüm Ekle/Düzenle Modalı */}
            <InfoModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                title={selectedDepartment ? `Bölüm Düzenle: ${selectedDepartment.name}` : "Yeni Bölüm Ekle"}
                showFooter={false}
            >
                <DepartmentForm
                    department={selectedDepartment}
                    onSave={handleFormSubmit}
                    onCancel={handleModalClose}
                    isNew={!selectedDepartment}
                    submitting={actionLoading}
                />
            </InfoModal>

            {/* Bölüm Personellerini Başka Bölüme Aktar Modalı (Silmeden) */}
            <InfoModal
                isOpen={isTransferModalOpen}
                onClose={handleModalClose}
                title="Bölüm Personellerini ve Yetkilerini Aktar"
                type="warning"
                onConfirm={handleTransferConfirm}
                isLoading={actionLoading}
                confirmText="Aktarımı Başlat"
                cancelText="Vazgeç"
                confirmDisabled={!transferOnlyTargetId}
            >
                {departmentToTransfer && (
                    <div className="space-y-4">
                        {transferModalError && (
                            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 p-3 rounded-xl text-xs">
                                {transferModalError}
                            </div>
                        )}
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                            <strong>{departmentToTransfer.name}</strong> bölümündeki{' '}
                            <strong>{departmentToTransfer.totalEmployeeCount || 0} personeli</strong> başka bir bölüme topluca aktarmak için hedef bölümü seçin:
                        </p>

                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                Hedef Bölüm Seçin <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={transferOnlyTargetId}
                                onChange={(e) => setTransferOnlyTargetId(e.target.value)}
                                className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="">-- Aktarılacak Bölümü Seçin --</option>
                                {availableTargetDepartmentsForTransfer.map(d => (
                                    <option key={d.id} value={d.id}>
                                        {d.name} ({d.activeEmployeeCount || 0} Aktif Personel)
                                    </option>
                                ))}
                            </select>
                        </div>

                        <label className="flex items-start gap-2.5 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={transferDataScope}
                                onChange={(e) => setTransferDataScope(e.target.checked)}
                                className="mt-0.5 h-4 w-4 text-indigo-600 rounded border-gray-300"
                            />
                            <span className="text-xs text-gray-700 dark:text-gray-300">
                                <strong>Veri Görme Kapsamı (DataScope) yetkilerini de kopyala:</strong> Bu bölümü görmeye yetkili yöneticiler hedef bölümü de görebilsin.
                            </span>
                        </label>
                    </div>
                )}
            </InfoModal>

            {/* Akıllı Silme & Aktararak Silme Onay Modalı */}
            <InfoModal
                isOpen={isConfirmModalOpen}
                onClose={handleModalClose}
                title="Bölüm Silme Onayı"
                type="confirm"
                onConfirm={handleDeleteConfirm}
                isLoading={actionLoading}
                confirmText={deleteNeedsTransfer ? "Personelleri Aktar ve Bölümü Sil" : "Evet, Bölümü Sil"}
                cancelText="Vazgeç"
                confirmDisabled={deleteNeedsTransfer && !transferTargetId}
            >
                {departmentToDelete && (
                    <div className="space-y-4">
                        {deleteModalError && (
                            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 p-3 rounded-xl text-xs">
                                {deleteModalError}
                            </div>
                        )}

                        {deleteNeedsTransfer ? (
                            <>
                                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/60 text-amber-800 dark:text-amber-200 p-3.5 rounded-xl text-xs space-y-1.5">
                                    <p className="font-semibold text-sm">
                                        "{departmentToDelete.name}" bölümüne bağlı kayıtlar bulundu!
                                    </p>
                                    <p>
                                        Bu bölümde <strong>{departmentToDelete.activeEmployeeCount || 0} aktif</strong>
                                        {(departmentToDelete.inactiveEmployeeCount || 0) > 0 && `, ${departmentToDelete.inactiveEmployeeCount} pasif`} personel
                                        {(departmentToDelete.authorizedUserCount || 0) > 0 && ` ve ${departmentToDelete.authorizedUserCount} yöneticinin veri kapsamı (DataScope) yetkisi`} bulunmaktadır.
                                    </p>
                                    <p>
                                        Veri bütünlüğünün bozulmaması için bağlı personellerin ve veri kapsamı yetkilerinin aktarılacağı yeni bölümü seçerek silme işlemini tamamlayabilirsiniz:
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                        Personellerin ve Yetkilerin Aktarılacağı Hedef Bölüm <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={transferTargetId}
                                        onChange={(e) => setTransferTargetId(e.target.value)}
                                        className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                                    >
                                        <option value="">-- Hedef Bölüm Seçin --</option>
                                        {availableTargetDepartmentsForDelete.map(d => (
                                            <option key={d.id} value={d.id}>
                                                {d.name} ({d.activeEmployeeCount || 0} Aktif Personel)
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </>
                        ) : (
                            <div className="space-y-2">
                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                    <strong>"{departmentToDelete.name}"</strong> bölümünü kalıcı olarak silmek istediğinizden emin misiniz?
                                </p>
                                {(departmentToDelete.authorizedUserCount || 0) > 0 && (
                                    <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2.5 rounded-lg border border-amber-200 dark:border-amber-800/50">
                                        Not: Bu bölüm üzerinde tanımlı <strong>{departmentToDelete.authorizedUserCount} kullanıcının</strong> veri görme kapsamı (DataScope) eşleşmesi de kaldırılacaktır.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </InfoModal>
        </div>
    );
}
